import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { PdfJobDetail } from "features/pdfTools/pdfTools";

function StatTile({
  label,
  value,
  tone = "default",
  tooltip,
}: {
  label: string;
  value: number;
  tone?: "default" | "warning";
  tooltip?: string;
}) {
  const tile = (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        minWidth: 132,
        borderRadius: 2,
        ...(tone === "warning" && value > 0
          ? { bgcolor: "rgba(237,108,2,0.08)", borderColor: "warning.light" }
          : {}),
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
        {value.toLocaleString()}
      </Typography>
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip}>{tile}</Tooltip> : tile;
}

export default function JobSummaryPanel({ job }: { job: PdfJobDetail }) {
  const totals = job.summary?.totals ?? {};
  const perFile = job.summary?.per_file ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        <StatTile label="Pages processed" value={totals.pages ?? 0} />
        <StatTile
          label="Employee folders"
          value={totals.employee_folders ?? 0}
        />
        <StatTile label="Files created" value={totals.renamed ?? 0} />
        <StatTile
          label="Error pages"
          value={totals.error_pages ?? 0}
          tone="warning"
          tooltip="Pages where the employee name, SSN or year could not be read. Review them in the Workspace tab."
        />
        <StatTile
          label="Skipped pages"
          value={totals.skipped ?? 0}
          tooltip="Pages of a different document type (e.g. instruction pages) that were not processed."
        />
      </Box>

      {perFile.length > 0 && (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Input file</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Pages
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Renamed
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Error pages
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Skipped
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perFile.map((row) => (
                <TableRow key={row.file_name}>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography noWrap sx={{ fontSize: 13 }}>
                      {row.file_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.pages}</TableCell>
                  <TableCell align="right">{row.renamed}</TableCell>
                  <TableCell align="right">{row.error_pages}</TableCell>
                  <TableCell align="right">{row.skipped}</TableCell>
                  <TableCell>
                    {row.aborted ? (
                      <Typography variant="caption" color="error">
                        Aborted{row.abort_reason ? ` (${row.abort_reason})` : ""}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
